import { AVPlaybackStatus } from "expo-av";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { StyleSheet } from "react-native";
import "react-native-get-random-values";
import { VideoPlayerHandle, VideoPlayerProps } from "./types/";

// We copied that config from the original RTV rails site
const hlsConfig = {
  capLevelToPlayerSize: true,
  defaultAudioCodec: "mp4a.40.5",
  debug: false,
};

const hlsInstances: { [pid: string]: any } = {};
const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
  (
    {
      source,
      style,
      autoPlay = false,
      onStatusUpdate,
      onFullScreenChange,
      pid,
      onHover,
      poster,
      onStreamError,
      ...rest
    },
    ref
  ) => {
    const [isReady, setIsReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const fullScreenEventListenerHandler = (e) => {
      onFullScreenChange(document.fullscreenElement !== null);
    };

    const hoverEventListenerHandler = (e) => {
      onHover();
    };

    useImperativeHandle(ref, () => {
      const ref = videoRef.current;
      return {
        play: async () => ref.play().catch((err: Error) => {
          // AbortError is expected when the source changes before play() resolves;
          // MANIFEST_PARSED will call play() again once the new source is loaded.
          if (err.name !== "AbortError") throw err;
        }),
        pause: async () => ref.pause(),
        seekTo: (time: number) => {
          ref.currentTime = time;
        },
        setVolumeTo: (volume: number) => {
          ref.volume = volume;
        },
        setIsMuted: (isMuted: boolean) => {
          ref.muted = isMuted;
        },
        requestPictureInPicture: async () => {
          try {
            await ref?.requestPictureInPicture();
            return true;
          } catch (err) {
            return false;
          }
        },
        exitPictureInPicture: document.exitPictureInPicture,
        requestFullscreen: async () => {
          try {
            const container = document.querySelector(
              `#${pid} #video-player-container`
            );
            container.addEventListener(
              "fullscreenchange", // or onwebkitfullscreenchange
              fullScreenEventListenerHandler
            );

            const video = document.getElementsByTagName("video")[0];

            const fullscreenApi =
              container.requestFullscreen ||
              container.webkitRequestFullscreen ||
              container.mozRequestFullScreen ||
              container.msRequestFullscreen;

            const videoFullScreenApi = video.webkitEnterFullscreen;

            if (fullscreenApi != null) {
              fullscreenApi.call(container);
              return true;
            } else if (videoFullScreenApi != null) {
              videoFullScreenApi.call(video);
              return true;
            }

            return false;
          } catch (err) {
            console.log(err);
            return false;
          }
        },
        exitFullscreen: async () => {
          await document.exitFullscreen().then((_) => {
            const container = document.querySelector(
              `#${pid} #video-player-container`
            );
            container.removeEventListener(
              "fullscreenchange",
              fullScreenEventListenerHandler
            );
          });
        },
        isPlaying: async () => !ref.paused,
      };
    });

    const handleReadyForDisplay = (e: any) => {
      if (e.type === "loadeddata") {
        clearStartupWatchdog();
        setIsReady(true);
      }
    };

    const handleTimeUpdate = (e: any) => {
      const videoEvent = e.target as HTMLMediaElement;
      const { currentTime, duration } = videoEvent;
      onStatusUpdate &&
        onStatusUpdate({
          positionMillis: currentTime * 1000,
          durationMillis: duration * 1000,
          isLoaded: isReady,
          isPlaying: !videoEvent.paused,
          didJustFinish: duration && currentTime >= duration,
        } as AVPlaybackStatus);
    };

    useEffect(() => {
      handleTimeUpdate({ target: videoRef.current as HTMLMediaElement });
    }, [isReady]);

    // Watchdog: if we never reach the "loadeddata" event within this window,
    // assume the stream is wedged (e.g. a single non-fatal fragParsingError that
    // HLS.js silently swallows) and request a fresh source instead of spinning
    // forever.
    const STARTUP_TIMEOUT_MS = 10000;
    const startupWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearStartupWatchdog = () => {
      if (startupWatchdogRef.current) {
        clearTimeout(startupWatchdogRef.current);
        startupWatchdogRef.current = null;
      }
    };

    const createHlsInstance = () => {
      const Hls = window.Hls;
      const video = videoRef.current as HTMLMediaElement;

      // IMPORTANT: keep a local reference to this specific HLS instance and use
      // it inside all event handlers. Previously the handlers referenced the
      // global `hlsInstances[pid]`, which means a delayed event from a
      // destroyed instance (after a rapid source/channel change) could mutate
      // the new instance — that race produced "permanent spinner" / spurious
      // fragParsingError. The ownership guard discards events that don't
      // belong to the currently-mounted instance.
      const hls = new Hls(hlsConfig);
      hlsInstances[pid] = hls;

      const isCurrentInstance = () => hlsInstances[pid] === hls;

      // Per HLS.js best practice (and matching Onspace player pattern):
      // loadSource is called only after the media element is attached.
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (!isCurrentInstance()) return;
        hls.loadSource(source);
      });

      // play() is called here instead of relying on the <video autoPlay> attribute.
      // The autoPlay attribute causes the browser to call play() before HLS.js has
      // taken control, which results in an AbortError and the video never starting.
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!isCurrentInstance()) return;
        if (autoPlay) {
          video.play().catch((err: Error) => {
            if (err.name !== "AbortError") {
              console.error("HLS play error:", err);
            }
          });
        }
      });

      // ################################
      //   DON'T REMOVE THIS CODE BELOW
      // ################################
      // It used to fix an error happening when some packets are missing.
      // The player stopped and nerver played again. The code below fixed that issue, by reloading the player.
      // On chrome web, the video replayed at the same position, but on chrome android, it replayed from the beginning.
      // Removing it fixed the issue on chrome android, and I didn't notice any issue on chrome web anymore.
      // My guess is that sources from Onspace have a better quality now. Or another change somewhere else fixed the issue.
      // I think it's safe to remove it, but I'm keeping it here just in case.

      // hlsInstances[pid].on(Hls.Events.ERROR, (event, data) => {
      //   console.error("HLS ERROR", data);
      //   hlsInstances[pid].destroy();
      //   // We noticed that sometimes hls.js fails to load the m3u8 file. So we try to load it again after 500ms
      //   // Weird 🤷‍♂️
      //   setTimeout(() => {
      //     createHlsInstance();
      //   }, 500);
      // });
      // ################################
      //   DON'T REMOVE THIS CODE ABOVE
      // ################################

      // Track recovery state for this HLS instance:
      // - mediaErrorRecovered: HLS.js media error recovery can only be attempted once
      // - nonFatalErrors: if HLS.js keeps reporting non-fatal errors without ever loading
      //   (e.g., expired signed URL causing Akamai to return bad data), escalate after
      //   a threshold and request a fresh source instead of waiting forever.

      let mediaErrorRecovered = false;
      let nonFatalErrors = 0;
      const NON_FATAL_ERROR_THRESHOLD = 5;

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!isCurrentInstance()) return;
        console.error("[VideoPlayer] HLS error:", data.type, data.details, "fatal:", data.fatal, "source:", source);

        if (!data.fatal) {
          nonFatalErrors++;
          if (nonFatalErrors >= NON_FATAL_ERROR_THRESHOLD) {
            console.error("[VideoPlayer] Too many non-fatal HLS errors — requesting fresh source");
            clearStartupWatchdog();
            hls.destroy();
            if (hlsInstances[pid] === hls) delete hlsInstances[pid];
            onStreamError?.();
          }
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaErrorRecovered) {
          // Attempt HLS.js built-in codec/buffer recovery once.
          console.warn("[VideoPlayer] Fatal media error — attempting recoverMediaError()");
          mediaErrorRecovered = true;
          hls.recoverMediaError();
        } else {
          // For network errors (e.g., expired signed URL → 403) or unrecoverable
          // media errors, destroy the instance and request a fresh signed URL.
          console.error("[VideoPlayer] Fatal HLS error — requesting fresh source");
          clearStartupWatchdog();
          hls.destroy();
          if (hlsInstances[pid] === hls) delete hlsInstances[pid];
          onStreamError?.();
        }
      });

      hls.attachMedia(video);

      // Arm the startup watchdog. handleReadyForDisplay clears it when the
      // browser fires "loadeddata".
      clearStartupWatchdog();
      startupWatchdogRef.current = setTimeout(() => {
        if (!isCurrentInstance()) return;
        console.error(
          "[VideoPlayer] Startup timeout — no loadeddata after",
          STARTUP_TIMEOUT_MS,
          "ms; requesting fresh source"
        );
        hls.destroy();
        if (hlsInstances[pid] === hls) delete hlsInstances[pid];
        onStreamError?.();
      }, STARTUP_TIMEOUT_MS);
    };

    const removeInstance = () => {
      clearStartupWatchdog();
      if (hlsInstances[pid]) {
        // Use destroy() not stopLoad(): destroy() also detaches the media
        // element, removes listeners, and releases the MediaSource. stopLoad()
        // only pauses fragment loading and leaves the instance attached.
        hlsInstances[pid].destroy();
        delete hlsInstances[pid];
      }
    };

    useEffect(() => {
      if (!source) return;
      if (!videoRef.current) return;
      const video = videoRef.current as HTMLMediaElement;
      const Hls = window.Hls;
      // Reset readiness so the spinner is shown again while the new source
      // loads and so the startup watchdog has a clean baseline.
      setIsReady(false);
      if (Hls.isSupported() && source.includes(".m3u8")) {
        if (hlsInstances[pid]) {
          hlsInstances[pid].destroy();
          delete hlsInstances[pid];
        }
        createHlsInstance();
      } else {
        video.src = source;
        if (autoPlay) {
          video.play().catch((err) => {
            if (err.name !== "AbortError") {
              console.error("Video play error:", err);
            }
          });
        }
      }
    }, [source]);

    useEffect(() => {
      videoRef.current?.addEventListener(
        "mousemove",
        hoverEventListenerHandler
      );
      videoRef.current?.addEventListener(
        "seeking",
        () => setIsReady(false),
        false
      );
      videoRef.current?.addEventListener(
        "seeked",
        () => setIsReady(true),
        false
      );
      return () => {
        videoRef.current?.removeEventListener(
          "mousemove",
          hoverEventListenerHandler
        );
        videoRef.current?.removeEventListener("seeking", () =>
          setIsReady(false)
        );
        videoRef.current?.removeEventListener("seeked", () => setIsReady(true));
        removeInstance();
      };
    }, [videoRef]);

    return (
      <video
        ref={videoRef}
        crossOrigin="true"
        playsInline // required to tell iOS safari to play inline and not in fullscreen
        poster={poster}
        onDurationChange={handleTimeUpdate}
        onTimeUpdate={handleTimeUpdate}
        onLoadedData={handleReadyForDisplay}
        onPause={handleTimeUpdate}
        controls={false}
        style={{ ...styles.container, ...style }}
        {...rest}
      />
    );
  }
);

export default VideoPlayer;

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
});
